import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Ear, Mic, PhoneCall } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import useSocket from "../../hooks/useWebsocket";
import Pagination from "../../components/Table/pagination";
import { TopDisappearingMessage } from "../../utils/Common";

const PAGE_SIZE = 10;


// call_record_vf is the one field that's reliably unique per call — event_id can be
// missing/shared across the simultaneous calls a predictive dialer places at once.
const sameCall = (a, b) => !!a.call_record_vf && a.call_record_vf === b.call_record_vf;

const STATUS_CLASSES = {
    initiated: "bg-blue-100 text-blue-700",
    active: "bg-green-100 text-green-700",
    ringing: "bg-yellow-100 text-yellow-700",
    completed: "bg-gray-100 text-gray-600",
    failed: "bg-red-100 text-red-600",
    missed: "bg-red-100 text-red-600",
    answered: "bg-green-100 text-green-700",
};

const LiveCallReport = () => {
    const { auth } = useAuth();
    const [liveCallData, setLiveCallData] = useState([]);
    const [page, setPage] = useState(1);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [destinationTypeFilter, setDestinationTypeFilter] = useState("");
    const [lastUpdated, setLastUpdated] = useState(null);

    const [isReady, val, send] = useSocket();

    useEffect(() => {
        if (!val) return;
        console.log("📨 Socket message:", val);
    }, [val]);

    // Initial fetch when socket connects
    useEffect(() => {
        if (!isReady || !send) return;
        send(JSON.stringify({ type: "get_live_calls" }));
    }, [isReady]);

    useEffect(() => {
        if (!val) return;

        // Initial load — has method field, filter applies
        if (val.type === "initial_cache_data" && Array.isArray(val.data)) {
            const autocallData = val.data.filter((item) => item.method === "autocallReport");
            setLiveCallData(autocallData);
            setLastUpdated(new Date());
            setPage(1);
            return;
        }

        // Real-time Pub/Sub — no method field, use data directly
        if (val.type === "cache_data") {
            const { action, data } = val;

            if (action === "add" && Array.isArray(data)) {
                setLiveCallData((prev) => {
                    const updated = [...prev];
                    data.forEach((newItem) => {
                        const idx = updated.findIndex((r) => sameCall(r, newItem));
                        if (idx !== -1) {
                            updated[idx] = newItem;
                        } else {
                            updated.push(newItem);
                        }
                    });
                    return updated;
                });
                setLastUpdated(new Date());
            } else if (action === "update" && Array.isArray(data)) {
                setLiveCallData((prev) => {
                    const updated = [...prev];
                    data.forEach((newItem) => {
                        const idx = updated.findIndex((r) => sameCall(r, newItem));
                        if (idx !== -1) {
                            updated[idx] = newItem;
                        } else {
                            updated.push(newItem);
                        }
                    });
                    return updated;
                });
                setLastUpdated(new Date());
            } else if (action === "delete") {
                setLiveCallData((prev) =>
                    prev.filter((item) => item.call_record_vf !== val.call_record_vf)
                );
                setLastUpdated(new Date());
            }
        }
    }, [val]);

    const filteredData = useMemo(() => {
        let data = liveCallData;
        if (searchText) {
            const lower = searchText.toLowerCase();
            data = data.filter(
                (item) =>
                    (item.agent_name || "").toLowerCase().includes(lower) ||
                    (item.customer || "").includes(searchText) ||
                    (item.DID || "").includes(searchText) ||
                    (item.agent_number || "").includes(searchText)
            );
        }
        if (statusFilter) {
            data = data.filter(
                (item) => (item.call_status || "").toLowerCase() === statusFilter.toLowerCase()
            );
        }
        if (destinationTypeFilter) {
            data = data.filter(
                (item) => (item.destination_type || "") === destinationTypeFilter
            );
        }
        return data;
    }, [liveCallData, searchText, statusFilter, destinationTypeFilter]);

    const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
    const paginatedData = filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const pagination = {
        current_page: page,
        total_pages: totalPages,
        total_records: filteredData.length,
    };

    const PaginationControl = (type, number = null) => {
        if (type === "page" && number) setPage(number);
        if (type === "prev" && page > 1) setPage((p) => p - 1);
        if (type === "next" && page < totalPages) setPage((p) => p + 1);
        if (type === "last") setPage(totalPages);
        if (type === "first") setPage(1);
    };

    const isProperAgentNumber = (num) => /^\d+$/.test(num || "");

    const isMonitorable = (call) =>
        isProperAgentNumber(call.agent_number) &&
        ["active", "answered"].includes((call.call_status || "").toLowerCase());

    const monitorCall = async (call, listenMode, actionLabel) => {
        try {
            console.log("Live call -------------->>> : ",call)
            const eventId = call.call_id || call.event_id || call.uid || "" || call.call_record_vf;
            const callType = eventId.startsWith("Campaign") ? "AUTOCALL" : "VSIPOUT";
            const sourceId = call.event_id || call.uid || "";
            const monitoring_id = crypto.randomUUID();
            await axios.post(
                process.env.REACT_APP_API_URL + "/live-call-monitoring/",
                {
                    outbound_caller_id: call.DID,
                    listen_call_id: eventId,
                    listen_mode: listenMode,
                    channel_id: call.channel_id ?? 1,
                    user_id: auth?.user_details?.id,
                    call_monitor_agent: auth?.user_details?.vsip,
                    call_type: callType,
                    event_id: monitoring_id,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${auth?.accessToken}`,
                    },
                    withCredentials: true,
                }
            );
            TopDisappearingMessage("top-end", 2000, "success", `${actionLabel} call with ${call.agent_name || call.agent_number}`);
        } catch (err) {
            console.error(err);
            TopDisappearingMessage("top-end", 2000, "error", `Failed to ${actionLabel.toLowerCase()} call`);
        }
    };

    const handleListen = (call) => monitorCall(call, "LISTEN", "Listening to");
    const handleWhisper = (call) => monitorCall(call, "WHISPER", "Whispering to");
    const handleBarge = (call) => monitorCall(call, "BARGE", "Barging into");

    const getStatusBadge = (status) => {
        const key = (status || "").toLowerCase();
        const cls = STATUS_CLASSES[key] || "bg-gray-100 text-gray-600";
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${cls}`}>
                {status || "-"}
            </span>
        );
    };

    return (
        <>
            <div className="bg-white">
                {/* Header */}
                <div className="flex items-center justify-between pt-6 pb-2 px-2">
                    <div className="flex items-center gap-2">
                        <span className="relative flex h-3 w-3">
                            <span
                                className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isReady ? "bg-green-400" : "bg-red-400"
                                    }`}
                            ></span>
                            <span
                                className={`relative inline-flex rounded-full h-3 w-3 ${isReady ? "bg-green-500" : "bg-red-500"
                                    }`}
                            ></span>
                        </span>
                        <h2 className="text-lg font-bold text-gray-700">Live Call Report</h2>
                        {!isReady && (
                            <span className="text-xs text-red-400 ml-1">Connecting...</span>
                        )}
                    </div>
                    {lastUpdated && (
                        <span className="text-xs text-gray-400">
                            Last updated: {lastUpdated.toLocaleTimeString()}
                        </span>
                    )}
                </div>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-3 pt-4 pb-2 px-2">
                    <input
                        type="text"
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-64 pl-4 p-2.5"
                        placeholder="Search agent, customer, DID..."
                        value={searchText}
                        onChange={(e) => {
                            setSearchText(e.target.value);
                            setPage(1);
                        }}
                    />
                    <select
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPage(1);
                        }}
                    >
                        <option value="">All Status</option>
                        <option value="Initiated">Initiated</option>
                        <option value="Active">Active</option>
                        <option value="Ringing">Ringing</option>
                        <option value="Answered">Answered</option>
                        <option value="Completed">Completed</option>
                        <option value="Failed">Failed</option>
                        <option value="Missed">Missed</option>
                    </select>
                    <select
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
                        value={destinationTypeFilter}
                        onChange={(e) => {
                            setDestinationTypeFilter(e.target.value);
                            setPage(1);
                        }}
                    >
                        <option value="">All Destination Types</option>
                        <option value="IVR">IVR</option>
                        <option value="Department">Department</option>
                        <option value="Callback">Callback</option>
                        <option value="Autocall">Autocall</option>
                        <option value="VSIP Outgoing">VSIP Outgoing</option>
                    </select>
                </div>

                {/* Table */}
                <div className="flex flex-col mt-2">
                    <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="py-2 inline-block min-w-full sm:px-6 lg:px-8">
                            <div className="overflow-hidden">
                                <table className="min-w-full">
                                    <thead className="border-b bg-gray-50">
                                        <tr>
                                            <th className="text-sm font-bold text-gray-400 px-6 py-4 text-left">Sl No</th>
                                            <th className="text-sm font-bold text-gray-400 px-6 py-4 text-left">DID</th>
                                            <th className="text-sm font-bold text-gray-400 px-6 py-4 text-left">Customer</th>
                                            <th className="text-sm font-bold text-gray-400 px-6 py-4 text-left">Agent</th>
                                            <th className="text-sm font-bold text-gray-400 px-6 py-4 text-left">Agent No.</th>
                                            <th className="text-sm font-bold text-gray-400 px-6 py-4 text-left">Destination Type</th>
                                            <th className="text-sm font-bold text-gray-400 px-6 py-4 text-left">Status</th>
                                            <th className="text-sm font-bold text-gray-400 px-6 py-4 text-left">Initiated Time</th>
                                            <th className="text-sm font-bold text-gray-400 px-6 py-4 text-left">Call Monitoring</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedData.length === 0 && (
                                            <tr>
                                                <td
                                                    colSpan={9}
                                                    className="text-center text-gray-400 py-8"
                                                >
                                                    {isReady
                                                        ? "No live call data."
                                                        : "Connecting to live feed..."}
                                                </td>
                                            </tr>
                                        )}
                                        {paginatedData.map((call, index) => (
                                            <tr
                                                className="border-b hover:bg-gray-50"
                                                key={call.call_record_vf || call.event_id || index}
                                            >
                                                <td className="text-sm text-gray-900 px-6 py-4 whitespace-nowrap">
                                                    {(page - 1) * PAGE_SIZE + index + 1}
                                                </td>
                                                <td className="text-sm text-gray-900 px-6 py-4 whitespace-nowrap font-mono">
                                                    {call.DID || "-"}
                                                </td>
                                                <td className="text-sm text-gray-900 px-6 py-4 whitespace-nowrap">
                                                    {call.customer || "-"}
                                                </td>
                                                <td className="text-sm text-gray-900 font-bold px-6 py-4 whitespace-nowrap">
                                                    {isProperAgentNumber(call.agent_number) ? (call.agent_name || "") : ""}
                                                </td>
                                                <td className="text-sm text-gray-900 px-6 py-4 whitespace-nowrap">
                                                    {isProperAgentNumber(call.agent_number) ? call.agent_number : ""}
                                                </td>
                                                <td className="text-sm text-gray-900 px-6 py-4 whitespace-nowrap">
                                                    {call.destination_type || "-"}
                                                </td>
                                                <td className="text-sm px-6 py-4 whitespace-nowrap">
                                                    {getStatusBadge(call.call_status)}
                                                </td>
                                                <td className="text-sm text-gray-900 px-6 py-4 whitespace-nowrap">
                                                    {call.call_initiated_time || "-"}
                                                </td>
                                                <td className="text-sm px-6 py-4 whitespace-nowrap">
                                                    {isMonitorable(call) ? (
                                                        <div className="flex items-center gap-3">
                                                            <button
                                                                title="Listen"
                                                                className="text-blue-600 hover:text-blue-700 transition-colors"
                                                                onClick={() => handleListen(call)}
                                                            >
                                                                <Ear size={18} />
                                                            </button>
                                                            <button
                                                                title="Whisper"
                                                                className="text-amber-600 hover:text-amber-700 transition-colors"
                                                                onClick={() => handleWhisper(call)}
                                                            >
                                                                <Mic size={18} />
                                                            </button>
                                                            <button
                                                                title="Barge"
                                                                className="text-red-600 hover:text-red-700 transition-colors"
                                                                onClick={() => handleBarge(call)}
                                                            >
                                                                <PhoneCall size={18} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-300">-</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div className="p-8">
                                    <Pagination
                                        pagination_data={pagination}
                                        PaginationControl={PaginationControl}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default LiveCallReport;